import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CalendarProvider } from '@/hooks/useIntegrations'
import { Loader2, Calendar } from 'lucide-react'

export const IntegrationCard = ({ 
  providerKey, 
  configuredProviders, 
  connectingProvider, 
  isCheckingBackendConfig, 
  connectCalendarMutation, 
  handleConnectCalendar, 
  PROVIDER_CONFIGS 
}: { 
  providerKey:  CalendarProvider, 
  configuredProviders: string[], 
  connectingProvider: CalendarProvider | null, 
  isCheckingBackendConfig: boolean, 
  connectCalendarMutation: any, 
  handleConnectCalendar: (provider: CalendarProvider) => void, 
  PROVIDER_CONFIGS: Record<CalendarProvider, {
    name: string;
    icon?: React.ReactNode;
    checkKey: string;
  }> 
}) => {
  const provider = PROVIDER_CONFIGS[providerKey];
  const isProviderConfigured = configuredProviders.includes(
    provider.checkKey
  );
  const isConnecting = connectingProvider === providerKey;

  const buttonDisabled =
    isCheckingBackendConfig ||
    (!isCheckingBackendConfig && !isProviderConfigured) ||
    isConnecting;

  let badgeMessage = "";
  let currentBadgeClassName = "mt-1 text-xs";
  let reasonMessage = "";
  let reasonMessageClassName = "text-xs mt-2 text-center";

  // Only show configuration messages after backend check is complete
  if (!isCheckingBackendConfig && !isProviderConfigured) {
    badgeMessage = "Admin setup needed";
    currentBadgeClassName +=
      " text-orange-600 border-orange-300 bg-orange-50";
    reasonMessage = `Connection unavailable until backend is configured for ${provider.name}.`;
    reasonMessageClassName += " text-red-500";
  }

  let cardClassName =
    "p-5 border rounded-lg flex flex-col justify-between transition-all duration-200 bg-white hover:shadow-md";

  if (isConnecting) {
    cardClassName =
      "p-5 border rounded-lg flex flex-col justify-between transition-all duration-200 bg-white border-blue-300 shadow-lg";
  }

  let buttonClassName =
    "w-full transition-all duration-200 bg-blue-600 hover:bg-blue-700 text-white";

  if (!isProviderConfigured || connectCalendarMutation.isPending) {
    buttonClassName += " cursor-not-allowed";
  }
  return (
    <div key={providerKey} className={cardClassName}>
    <div>
      <div className="flex items-center space-x-2">
        {provider.icon}
        <h3 className="text-lg font-semibold text-gray-700">
          {provider.name}
        </h3>
      </div>
      {badgeMessage && (
        <Badge
          variant="outline"
          className={currentBadgeClassName}
        >
          {badgeMessage}
        </Badge>
      )}
      <p className="text-sm mt-1 mb-4 text-gray-500">
        {`Allow access to your ${provider.name} to automate appointment management.`}
      </p>
      {providerKey === "calendly" && (
        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
          <strong>Note:</strong> Calendly integration
          requires a paid account (Professional, Team, or
          Enterprise).
        </div>
      )}
    </div>
    <Button
      variant="default"
      onClick={() => handleConnectCalendar(providerKey)}
      disabled={buttonDisabled}
      className={buttonClassName}
    >
      {isConnecting ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Connecting...
        </>
      ) : (
        <>
          <Calendar className="mr-2 h-4 w-4" />
          Connect {provider.name}
        </>
      )}
    </Button>
    {reasonMessage && (
      <p className={reasonMessageClassName}>
        {reasonMessage}
      </p>
    )}
  </div>
  )
}